import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type WheelEvent } from "react";

type ControlName = "Frequency" | "Bandwidth" | "Noise reduction";
type RoundState = "idle" | "tuning" | "locked";

const labels: ControlName[] = ["Frequency", "Bandwidth", "Noise reduction"];
const recoveredMessages = [
  "THE SHED REMEMBERS",
  "JEFF IS STILL ONLINE",
  "75,000,000 SIGNALS RECEIVED",
  "BUILD SOMETHING STRANGE",
  "ESTABLISHED 2015"
];

function clamp(value: number) {
  return Math.min(1, Math.max(0, value));
}

function makeTargets() {
  return [Math.random(), Math.random(), Math.random()];
}

function signalStrength(values: number[], targets: number[]) {
  const error = values.reduce((sum, value, index) => sum + Math.abs(value - targets[index]), 0) / 3;
  return clamp(1 - error * 1.8);
}

export default function RadioTuningGame() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | undefined>(undefined);
  const visibleRef = useRef(true);
  const documentVisibleRef = useRef(!document.hidden);
  const reduceMotionRef = useRef(false);
  const dragRef = useRef<{ index: number; x: number; y: number; value: number } | null>(null);
  const valuesRef = useRef([0.28, 0.5, 0.72]);
  const targetsRef = useRef(makeTargets());
  const [values, setValues] = useState(valuesRef.current);
  const [state, setState] = useState<RoundState>("idle");
  const [message, setMessage] = useState(recoveredMessages[0]);
  const [playable, setPlayable] = useState(true);
  const strength = signalStrength(values, targetsRef.current);

  const setControl = useCallback((index: number, next: number) => {
    const updated = valuesRef.current.map((value, valueIndex) => valueIndex === index ? clamp(next) : value);
    valuesRef.current = updated;
    setValues(updated);
    if (state !== "locked") setState("tuning");
    if (signalStrength(updated, targetsRef.current) > 0.94) {
      setMessage(recoveredMessages[Math.floor(Math.random() * recoveredMessages.length)]);
      setState("locked");
    }
  }, [state]);

  const retune = useCallback(() => {
    targetsRef.current = makeTargets();
    valuesRef.current = [0.28, 0.5, 0.72];
    setValues(valuesRef.current);
    setState("idle");
  }, []);

  const draw = useCallback((now: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || !visibleRef.current || !documentVisibleRef.current) return;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    const current = signalStrength(valuesRef.current, targetsRef.current);
    ctx.clearRect(0, 0, w, h);
    ctx.strokeStyle = "rgba(67,255,117,0.08)";
    for (let y = 0; y < h; y += 4) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    ctx.strokeStyle = current > 0.94 ? "#43ff75" : "rgba(67,255,117,0.75)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    const noise = reduceMotionRef.current ? 0 : (1 - current) * 18;
    const phase = reduceMotionRef.current ? 0 : now / 420;
    for (let x = 0; x < w; x += 3) {
      const t = x / w;
      const wave = Math.sin(t * Math.PI * 6 + phase) * (5 + current * 14);
      const jitter = Math.sin((x + (reduceMotionRef.current ? 0 : now / 14)) * 1.7) * noise;
      const y = h * 0.52 + wave + jitter;
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    frameRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => {
      reduceMotionRef.current = media.matches;
      if (media.matches) setState((current) => current === "tuning" ? "idle" : current);
    };
    updateMotion();
    media.addEventListener("change", updateMotion);
    return () => media.removeEventListener("change", updateMotion);
  }, []);

  useEffect(() => {
    const wrapper = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return;
    const resize = () => {
      const rect = wrapper.getBoundingClientRect();
      const canvasRect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const nextPlayable = rect.width >= 280 && rect.height >= 118;
      setPlayable(nextPlayable);
      canvas.width = Math.max(1, Math.floor(canvasRect.width * dpr));
      canvas.height = Math.max(1, Math.floor(canvasRect.height * dpr));
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(wrapper);
    resize();
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const wrapper = wrapRef.current;
    if (!wrapper) return;
    const observer = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
      cancelAnimationFrame(frameRef.current ?? 0);
      if (entry.isIntersecting && documentVisibleRef.current) frameRef.current = requestAnimationFrame(draw);
    }, { threshold: 0.05 });
    observer.observe(wrapper);
    const onVisibility = () => {
      documentVisibleRef.current = !document.hidden;
      cancelAnimationFrame(frameRef.current ?? 0);
      if (!document.hidden && visibleRef.current) frameRef.current = requestAnimationFrame(draw);
    };
    document.addEventListener("visibilitychange", onVisibility);
    frameRef.current = requestAnimationFrame(draw);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      cancelAnimationFrame(frameRef.current ?? 0);
    };
  }, [draw]);

  useEffect(() => {
    const endDrag = () => { dragRef.current = null; };
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    return () => {
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, []);

  const hotspots = useMemo(() => [
    { x: 24, y: 78 },
    { x: 54, y: 78 },
    { x: 92, y: 78 }
  ], []);

  const startDrag = (event: PointerEvent<HTMLButtonElement>, index: number) => {
    if (!playable || reduceMotionRef.current) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { index, x: event.clientX, y: event.clientY, value: valuesRef.current[index] };
    setState("tuning");
  };

  const moveDrag = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || !playable || reduceMotionRef.current) return;
    const delta = (event.clientX - drag.x - (event.clientY - drag.y)) / 160;
    setControl(drag.index, drag.value + delta);
  };

  const adjustBy = (index: number, delta: number) => {
    if (!playable || reduceMotionRef.current) return;
    setControl(index, valuesRef.current[index] + delta);
  };

  const onWheel = (event: WheelEvent<HTMLButtonElement>, index: number) => {
    if (document.activeElement !== event.currentTarget || !playable || reduceMotionRef.current) return;
    event.preventDefault();
    adjustBy(index, event.deltaY > 0 ? -0.025 : 0.025);
  };

  return (
    <div ref={wrapRef} className="radio-tuning-game" aria-label="Tune the Signal interactive radio controls">
      <canvas ref={canvasRef} className="radio-waveform" aria-hidden="true" />
      <div className="radio-readout" aria-live="polite">
        {state === "locked" ? (
          <>
            <strong>TRANSMISSION LOCKED</strong>
            <span>MESSAGE RECOVERED</span>
            <em>{message}</em>
            <button type="button" onClick={retune}>Retune</button>
          </>
        ) : (
          <>
            <strong>SIGNAL DETECTED - TUNE FREQUENCY</strong>
            <span>{`${Math.round(87 + values[0] * 21)}.${Math.round(values[1] * 9)} FM`}</span>
          </>
        )}
      </div>
      <div className="signal-bars" aria-hidden="true">
        {[0.2, 0.4, 0.6, 0.8, 0.94].map((level) => <span key={level} className={strength >= level ? "lit" : ""} />)}
      </div>
      {hotspots.map((spot, index) => (
        <button
          className="radio-knob-zone"
          type="button"
          key={labels[index]}
          role="slider"
          aria-label={`${labels[index]} knob, ${Math.round(values[index] * 100)} percent`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(values[index] * 100)}
          disabled={!playable}
          style={{ left: `${spot.x}%`, top: `${spot.y}%`, "--turn": `${values[index] * 270 - 135}deg` } as React.CSSProperties}
          onPointerDown={(event) => startDrag(event, index)}
          onPointerMove={moveDrag}
          onWheel={(event) => onWheel(event, index)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              dragRef.current = null;
              setState("idle");
            }
            if (event.key === "ArrowUp" || event.key === "ArrowRight") {
              event.preventDefault();
              adjustBy(index, 0.035);
            }
            if (event.key === "ArrowDown" || event.key === "ArrowLeft") {
              event.preventDefault();
              adjustBy(index, -0.035);
            }
          }}
        >
          <span />
        </button>
      ))}
    </div>
  );
}
